import { Component, type ErrorInfo, type ReactNode } from 'react'

interface Props {
  children: ReactNode
  /** Named so the fallback can say which part of the page failed. */
  section?: string
}

interface State {
  error: Error | null
}

/**
 * Without this, a render error unmounts the whole tree and leaves a white
 * page with nothing in the UI to explain it. Wrapping per section means one
 * broken widget degrades to a message while the rest of the dashboard keeps
 * working — which matters here, because a hiring dashboard is read for
 * decisions, and a blank screen is indistinguishable from "no candidates".
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null }

  static getDerivedStateFromError(error: Error): State {
    return { error }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // eslint-disable-next-line no-console -- until a real error sink exists
    console.error(`[${this.props.section ?? 'app'}] render failed`, error, info.componentStack)
  }

  render() {
    if (!this.state.error) return this.props.children

    return (
      <section className="card sec" style={{ padding: '18px 20px' }} role="alert">
        <h2 className="h2" style={{ fontSize: '14.5px' }}>
          {this.props.section ? `${this.props.section} could not be displayed` : 'Something went wrong'}
        </h2>
        <p className="sub" style={{ marginTop: 6 }}>
          The rest of the dashboard is unaffected. Reload to try again.
        </p>
        <button
          className="btn"
          type="button"
          style={{ marginTop: 12 }}
          onClick={() => this.setState({ error: null })}
        >
          Retry this section
        </button>
      </section>
    )
  }
}
